import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { State } from "country-state-city";
import { ProcedureFlowServices } from "../../../services/ProcedureFlowServices";
import { HospitalServices } from "../../../services/HospitalServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import {
  sanitizeDecimalCostInput,
  hasUserFilledAnyCostMappingInput,
} from "../../../utils/sanitizeCostInput";
import { toast } from "react-toastify";
import deleteIcon from "../../../assets/deleteIcon.svg";
import downArrowNew from "../../../assets/downArrowNew.svg";

const INDIAN_STATES = State.getStatesOfCountry("IN").map((s) => s.name);

const DEFAULT_STRATIFICATION_NAMES = [
  "Routine Ward",
  "HDU",
  "ICU-without Ventilator",
  "ICU- with Ventilator",
];

const defaultStratRows = () =>
  DEFAULT_STRATIFICATION_NAMES.map((name) => ({ name, cost: "" }));

/** proceduremaster flags may be boolean, "true"/"false", 0/1, or null */
function isTruthyFlag(value) {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null || value === "") return false;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  }
  return false;
}

/** Nested procedure master on scheme-procedure-cost-list rows */
function getProcedureMaster(item) {
  if (!item || typeof item !== "object") return {};
  const sm = item.proceduresschememaster ?? item.procedureSchemeMaster ?? null;
  if (!sm || typeof sm !== "object") return {};
  return sm.proceduremaster ?? sm.procedureMaster ?? {};
}

/** Stable JSON for “dirty” check — tiers sorted by tierNo, normalized strings */
function serializeCostMappingForm(stratification, tiers, implantRows, stateId) {
  const tierPart = [...(tiers || [])]
    .slice()
    .sort((a, b) => (Number(a.tierNo) || 0) - (Number(b.tierNo) || 0))
    .map((t) => ({
      tierNo: t.tierNo,
      cost: String(t.cost ?? "").trim(),
      rows: (t.rows || []).map((r) => ({
        name: String(r?.name ?? "").trim(),
        cost: String(r?.cost ?? "").trim(),
      })),
    }));
  const implants = [...(implantRows || [])].map((r) => ({
    name: String(r?.name ?? "").trim(),
    cost: String(r?.cost ?? "").trim(),
  }));
  return JSON.stringify({
    stratification: !!stratification,
    stateId: String(stateId ?? "").trim(),
    tiers: tierPart,
    implants,
  });
}

export default function ProcedureCostMappingEdited() {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const {
    procedureName,
    scheme,
    schemeId,
    procedureCostMapping: procedureCostMappingFromState,
    costItemId,
    costMappingId,
    state: selectedStateFromState,
    procedureId,
    specialtyName,
    isStratification: isStratificationFromNav,
    isImplant: isImplantFromNav,
    /** From Manage Procedure — same proceduremaster as cost list row */
    procedureMaster: procedureMasterFromNav,
    /** Full cost row from Manage Procedure when list refetch is paginated */
    costRowSnapshot: costRowSnapshotFromNav,
  } = locationState || {};

  // Persist full navigation state so we always have costItemId for save (ref can be set before state updates)
  const navStateRef = useRef(null);
  if (locationState && typeof locationState === "object") {
    navStateRef.current = locationState;
  }
  const initialFormSnapshotRef = useRef(null);
  const stratRowNameBaselineRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [costItem, setCostItem] = useState(null);
  const [stateId, setStateId] = useState(selectedStateFromState || "");
  const [hospitalName, setHospitalName] = useState("");
  const [stratification, setStratification] = useState(false);
  const [implantRows, setImplantRows] = useState([]);
  const [implantSectionExpanded, setImplantSectionExpanded] = useState(false);
  /** From procedure master: show implant section only when isImplant is true */
  const [masterImplantEnabled, setMasterImplantEnabled] = useState(false);
  const [tiers, setTiers] = useState([
    { tierNo: 1, rows: defaultStratRows(), cost: "" },
    { tierNo: 2, rows: defaultStratRows(), cost: "" },
    { tierNo: 3, rows: defaultStratRows(), cost: "" },
  ]);

  const procedureCostMapping = procedureCostMappingFromState || "STATE";

  useEffect(() => {
    if (!schemeId || !procedureCostMapping) {
      initialFormSnapshotRef.current = null;
      stratRowNameBaselineRef.current = null;
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    initialFormSnapshotRef.current = null;
    stratRowNameBaselineRef.current = null;
    const cidFromNav = navStateRef.current?.costItemId ?? costItemId;

    ProcedureFlowServices.getSchemeProcedureCostList({
      schemeId,
      procedureCostMapping,
    })
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.list || [];
        const cid = cidFromNav;
        const nav = navStateRef.current || {};
        let item = cid
          ? list.find((i) => String(i.id) === String(cid))
          : list.find((i) => (i.stateId || "") === (selectedStateFromState || "")) || list[0];

        const snap = nav.costRowSnapshot ?? costRowSnapshotFromNav;
        if (!item && snap && typeof snap === "object") {
          if (!cid || String(snap.id ?? "") === String(cid)) {
            item = snap;
          }
        }

        if (!item) {
          initialFormSnapshotRef.current = null;
          stratRowNameBaselineRef.current = null;
          const pmNav =
            nav.procedureMaster && typeof nav.procedureMaster === "object"
              ? nav.procedureMaster
              : {};
          setStratification(
            isTruthyFlag(nav.isStratification ?? isStratificationFromNav) ||
              isTruthyFlag(pmNav.isStratification)
          );
          setMasterImplantEnabled(
            isTruthyFlag(nav.isImplant ?? isImplantFromNav) ||
              isTruthyFlag(pmNav.isImplant)
          );
          if (cid) {
            toast.error(
              "Could not load this cost mapping row. Reopen from Manage Procedure."
            );
          }
          return;
        }

        setCostItem(item);
        setStateId(item.stateId || selectedStateFromState || "");

          const parseArray = (val) => {
            if (Array.isArray(val)) return val;
            if (typeof val === "string" && val.trim()) {
              try {
                const parsed = JSON.parse(val);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return [];
              }
            }
            return [];
          };

          // Build tiers from API: item has tier1, tier2, tier3, tier4, tier5
          const tierKeys = Object.keys(item).filter((k) => /^tier\d+$/.test(k));
          const tierNumbers = tierKeys
            .map((k) => parseInt(k.replace("tier", ""), 10))
            .filter((n) => !Number.isNaN(n))
            .sort((a, b) => a - b);
          const allTierCostsFromApi = tierNumbers.map((n) => {
            const tierKey = `tier${n}`;
            const tierData = item[tierKey];
            const cost = tierData?.cost != null ? String(tierData.cost) : "";
            const rowsFromTier =
              parseArray(tierData?.rows)?.length > 0
                ? parseArray(tierData.rows)
                : parseArray(tierData?.stratifications);
            return { tierNo: n, cost, rowsFromTier: Array.isArray(rowsFromTier) ? rowsFromTier : [] };
          });

          // Only keep tiers that actually have data (filter out null/empty tiers like tier3:null, tier4:null, tier5:null)
          const tierCostsFromApi = allTierCostsFromApi.filter(
            (t) => t.cost !== "" || t.rowsFromTier.length > 0
          );

          // Stratifications: check multiple sources (nrp field, top-level stratifications, per-tier embedded)
          const stratificationsFromApi = parseArray(item.stratifications);
          const hasStratificationData = stratificationsFromApi.length > 0;
          const hasPerTierStratification = tierCostsFromApi.some((t) => t.rowsFromTier.length > 0);

          // Check nrp field for per-tier stratification data (stored by our save logic)
          const nrpData = typeof item.nrp === "string" ? (() => { try { return JSON.parse(item.nrp); } catch { return item.nrp; } })() : item.nrp;
          const nrpTierStrats = nrpData?.tierStratifications || {};
          const hasNrpStratification = nrpData?.hasStratification === true && Object.keys(nrpTierStrats).length > 0;

          const toRow = (r) => ({
            name: String(r?.name ?? r?.stratificationName ?? "").trim(),
            cost: r?.cost != null && r?.cost !== "" ? String(r.cost) : "",
          });

          const pmNav =
            nav.procedureMaster && typeof nav.procedureMaster === "object"
              ? nav.procedureMaster
              : {};
          const pmItem = getProcedureMaster(item);
          const masterStratFromPm =
            isTruthyFlag(nav.isStratification ?? isStratificationFromNav) ||
            isTruthyFlag(pmNav.isStratification) ||
            isTruthyFlag(pmItem.isStratification);
          const masterImplantFromPm =
            isTruthyFlag(nav.isImplant ?? isImplantFromNav) ||
            isTruthyFlag(pmNav.isImplant) ||
            isTruthyFlag(pmItem.isImplant);

          const hasStratification =
            hasNrpStratification ||
            hasStratificationData ||
            hasPerTierStratification ||
            item.stratification === true ||
            item.hasStratification === true ||
            masterStratFromPm;

          let tiersInit;
          if (hasStratification) {
            if (hasNrpStratification) {
              // Per-tier stratification data from nrp field (most reliable source)
              tiersInit = tierCostsFromApi.map((t) => {
                const tierKey = `tier${t.tierNo}`;
                const nrpRows = parseArray(nrpTierStrats[tierKey]);
                return {
                  tierNo: t.tierNo,
                  rows: nrpRows.length ? nrpRows.map(toRow) : defaultStratRows(),
                  cost: t.cost,
                };
              });
            } else if (hasPerTierStratification) {
              // Stratification data is nested inside each tier object
              tiersInit = tierCostsFromApi.map((t) => ({
                tierNo: t.tierNo,
                rows: t.rowsFromTier.length ? t.rowsFromTier.map(toRow) : defaultStratRows(),
                cost: t.cost,
              }));
            } else if (hasStratificationData) {
              // Flat stratifications at top level: distribute across tiers using greedy cost-sum matching
              // The add page flattens tier rows in order (tier1 rows first, then tier2, etc.)
              // so we reconstruct by matching running sum to each tier's total cost
              const stratRows = stratificationsFromApi.map(toRow);
              const distributed = [];
              let stratIdx = 0;

              for (let t = 0; t < tierCostsFromApi.length; t++) {
                const targetCost = Number(tierCostsFromApi[t].cost || 0);
                const tierRows = [];
                let runningSum = 0;

                while (stratIdx < stratRows.length) {
                  const row = stratRows[stratIdx];
                  tierRows.push(row);
                  runningSum += Number(row.cost || 0);
                  stratIdx++;
                  if (targetCost > 0 && runningSum >= targetCost) break;
                }

                distributed.push(tierRows);
              }

              // Any remaining stratifications go to the last tier
              if (stratIdx < stratRows.length && distributed.length > 0) {
                distributed[distributed.length - 1].push(
                  ...stratRows.slice(stratIdx)
                );
              }

              tiersInit = tierCostsFromApi.length
                ? tierCostsFromApi.map((t, i) => ({
                    tierNo: t.tierNo,
                    rows: (distributed[i] || []).length ? distributed[i] : defaultStratRows(),
                    cost: t.cost,
                  }))
                : [
                    { tierNo: 1, rows: defaultStratRows(), cost: "" },
                    { tierNo: 2, rows: defaultStratRows(), cost: "" },
                    { tierNo: 3, rows: defaultStratRows(), cost: "" },
                  ];
            } else {
              tiersInit = tierCostsFromApi.length
                ? tierCostsFromApi.map((t) => ({
                    tierNo: t.tierNo,
                    rows: defaultStratRows(),
                    cost: t.cost,
                  }))
                : [
                    { tierNo: 1, rows: defaultStratRows(), cost: "" },
                    { tierNo: 2, rows: defaultStratRows(), cost: "" },
                    { tierNo: 3, rows: defaultStratRows(), cost: "" },
                  ];
            }
          } else {
            tiersInit =
              tierCostsFromApi.length > 0
                ? tierCostsFromApi.map((t) => ({
                    tierNo: t.tierNo,
                    rows: defaultStratRows(),
                    cost: t.cost,
                  }))
                : [
                    { tierNo: 1, rows: defaultStratRows(), cost: "" },
                    { tierNo: 2, rows: defaultStratRows(), cost: "" },
                    { tierNo: 3, rows: defaultStratRows(), cost: "" },
                  ];
          }
          stratRowNameBaselineRef.current = hasStratification
            ? tiersInit.map((t) =>
                (t.rows || []).map((r) => String(r?.name ?? "").trim())
              )
            : null;
          setTiers(tiersInit);

          setStratification(hasStratification);
          setMasterImplantEnabled(masterImplantFromPm);

          // Implants: may be at item.implants or nested; support array, single object, or JSON string
          let rawImplants =
            item.implants ??
            item.Implants ??
            item.implant ??
            item.proceduresschememaster?.implants;
          if (typeof rawImplants === "string" && rawImplants.trim()) {
            try {
              rawImplants = JSON.parse(rawImplants);
            } catch {
              rawImplants = null;
            }
          }
          const implantList = Array.isArray(rawImplants)
            ? rawImplants
            : rawImplants && typeof rawImplants === "object" && !Array.isArray(rawImplants)
            ? [rawImplants]
            : [];
          // Also support single implant at top level of item (e.g. item.implantName, item.implantCost)
          const hasTopLevelImplant =
            (item.implantName != null && item.implantName !== "") ||
            (item.implantCost != null && item.implantCost !== "");
          let implantRowsInit = [];
          if (masterImplantFromPm) {
            if (implantList.length > 0) {
              implantRowsInit = implantList.map((impl) => {
                const name =
                  impl.implantName ??
                  impl.name ??
                  impl.implant_name ??
                  impl.ImplantName ??
                  "";
                const costVal =
                  impl.implantCost ??
                  impl.cost ??
                  impl.implant_cost ??
                  impl.ImplantCost;
                return {
                  name: typeof name === "string" ? name : "",
                  cost: costVal != null && costVal !== "" ? String(costVal) : "",
                };
              });
            } else if (hasTopLevelImplant) {
              implantRowsInit = [
                {
                  name: String(item.implantName ?? item.ImplantName ?? ""),
                  cost:
                    item.implantCost != null || item.ImplantCost != null
                      ? String(item.implantCost ?? item.ImplantCost ?? "")
                      : "",
                },
              ];
            }
          }
          setImplantRows(implantRowsInit);
          setImplantSectionExpanded(implantRowsInit.length > 0);

          const stateIdForSnap = item.stateId || selectedStateFromState || "";
          initialFormSnapshotRef.current = serializeCostMappingForm(
            hasStratification,
            tiersInit,
            implantRowsInit,
            stateIdForSnap
          );
      })
      .catch((err) => {
        if (!cancelled) {
          initialFormSnapshotRef.current = null;
          stratRowNameBaselineRef.current = null;
          handleApiErrorResponses(err);
          toast.error("Failed to load procedure cost data.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
  }, [
    schemeId,
    procedureCostMapping,
    costItemId,
    selectedStateFromState,
    isStratificationFromNav,
    isImplantFromNav,
    procedureMasterFromNav,
    costRowSnapshotFromNav,
  ]);

  // When procedureCostMapping is HOSPITAL, fetch hospital list and resolve hospital name from costMappingId
  useEffect(() => {
    const costType = String(procedureCostMapping || "").toUpperCase();
    if (costType !== "HOSPITAL" || !costMappingId) {
      setHospitalName("");
      return;
    }
    let cancelled = false;
    HospitalServices.gethospitals("", 1, 1500)
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.list ?? [];
        const hosp = list.find(
          (h) =>
            String(h?.id ?? "") === String(costMappingId) ||
            String(h?.hospitalId ?? "") === String(costMappingId) ||
            String(h?.code ?? "") === String(costMappingId)
        );
        setHospitalName(hosp ? (hosp.name ?? hosp.hospitalName ?? "") : String(costMappingId));
      })
      .catch(() => setHospitalName(String(costMappingId)));
    return () => { cancelled = true; };
  }, [procedureCostMapping, costMappingId]);

  const removeTier = (index) => {
    setTiers((prev) => {
      if (prev.length <= 1) return prev;
      return prev
        .filter((_, i) => i !== index)
        .map((t, i) => ({ ...t, tierNo: i + 1 }));
    });
  };

  const removeTierRow = (tierIndex, rowIndex) => {
    setTiers((prev) => {
      const updated = [...prev];
      const rows = [...(updated[tierIndex].rows || [])];
      if (rows.length <= 1) return prev;
      rows.splice(rowIndex, 1);
      updated[tierIndex] = { ...updated[tierIndex], rows };
      return updated;
    });
  };

  const removeImplantRow = (index) => {
    setImplantRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) {
        setImplantSectionExpanded(false);
      }
      return next;
    });
  };

  const handleSave = async () => {
    const id = costItem?.id ?? navStateRef.current?.costItemId ?? costItemId;
    const idStr = id != null ? String(id) : "";
    if (!idStr) {
      toast.error("Cannot update: missing cost record id.");
      return;
    }
    const currentSnap = serializeCostMappingForm(
      stratification,
      tiers,
      implantRows,
      stateId
    );
    if (
      initialFormSnapshotRef.current != null &&
      currentSnap === initialFormSnapshotRef.current
    ) {
      toast.info("No changes to save.");
      return;
    }
    if (
      !hasUserFilledAnyCostMappingInput(
        stratification,
        tiers,
        implantRows,
        stratification ? stratRowNameBaselineRef.current : null
      )
    ) {
      toast.error(
        "Please fill at least one tier cost, stratification name or cost, or implant field before saving."
      );
      return;
    }
    setSaving(true);
    try {
      const tierPayload = {};
      tiers.forEach((tier, index) => {
        const cost = stratification
          ? (tier.rows || []).reduce((sum, r) => sum + Number(r?.cost || 0), 0)
          : Number(tier.cost || 0);

        const tierEntry = { cost };

        if (stratification) {
          const tierStrats = (tier.rows || [])
            .map((r) => ({
              name: (r?.name ?? "").trim(),
              cost: Number(r?.cost || 0),
            }))
            .filter((r) => r.name || r.cost);
          if (tierStrats.length) {
            tierEntry.stratifications = tierStrats;
          }
        }

        tierPayload[`tier${index + 1}`] = tierEntry;
      });
      // Implants: same shape as add flow (implantName, implantCost); we store as name/cost in state
      const implants = (implantRows || [])
        .map((r) => ({
          implantName: (r?.name ?? "").trim(),
          implantCost: Number(r?.cost || 0),
        }))
        .filter((r) => r.implantName || r.implantCost);
      // Stratifications: all tier rows when stratification is on (name, cost)
      const stratifications = stratification
        ? (tiers || [])
            .flatMap((t) => t.rows || [])
            .map((r) => ({
              name: (r?.name ?? "").trim(),
              cost: Number(r?.cost || 0),
            }))
            .filter((r) => r.name || r.cost)
        : [];
      // Store per-tier stratification breakdown in nrp field so it round-trips via the API
      const nrpPayload = stratification
        ? {
            hasStratification: true,
            tierStratifications: tiers.reduce((acc, tier, idx) => {
              const rows = (tier.rows || [])
                .map((r) => ({
                  name: (r?.name ?? "").trim(),
                  cost: Number(r?.cost || 0),
                }))
                .filter((r) => r.name || r.cost);
              if (rows.length) acc[`tier${idx + 1}`] = rows;
              return acc;
            }, {}),
          }
        : null;
      const payload = [
        {
          id: idStr,
          procedureCostMapping,
          stateId: stateId || costItem?.stateId,
          ...tierPayload,
          ...(masterImplantEnabled && implants.length ? { implants } : {}),
          ...(stratification && stratifications.length ? { stratifications } : {}),
          nrp: nrpPayload,
        },
      ];
      await ProcedureFlowServices.updateProcedureCost(payload);
      toast.success("Procedure cost updated successfully.");
      navigate("/dashboard/manage-procedure-page");
    } catch (err) {
      handleApiErrorResponses(err);
      toast.error("Failed to update procedure cost. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-b from-[#F4FBF8] to-[#EEF7F3] flex items-center justify-center font-lufga">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  const formSnapshotNow = serializeCostMappingForm(
    stratification,
    tiers,
    implantRows,
    stateId
  );
  const hasUnsavedChanges =
    initialFormSnapshotRef.current != null &&
    formSnapshotNow !== initialFormSnapshotRef.current;
  const hasAnyFilledCostField = hasUserFilledAnyCostMappingInput(
    stratification,
    tiers,
    implantRows,
    stratification ? stratRowNameBaselineRef.current : null
  );

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#F4FBF8] to-[#EEF7F3] flex justify-center py-10 font-lufga">
      <div className="w-full max-w-5xl px-6">
        <h2 className="text-l font-semibold text-gray-900 mb-8">
          Procedure - Cost Mapping
        </h2>

        <div className="space-y-6">
          <h1 className="font-lufga font-normal text-[50px] leading-[100%] tracking-[-0.07em] text-black whitespace-nowrap">
            Procedure - Cost Mapping
          </h1>

          <Dropdown placeholder="Procedure" value={procedureName} disabled />
          <div className="flex gap-6">
            <Dropdown placeholder="Scheme" value={scheme} disabled />
            {String(procedureCostMapping || "").toUpperCase() === "HOSPITAL" ? (
              <Dropdown
                placeholder="Hospital Name"
                value={hospitalName}
                disabled
                className="max-w-xs"
              />
            ) : (
              <StateDropdown
                value={stateId}
                onChange={setStateId}
                placeholder="State"
                disabled
              />
            )}
          </div>
          <Dropdown placeholder="Country" value="India" disabled />

          {/* Implant Section — only when procedure master has isImplant true */}
          {masterImplantEnabled && (
            <div className="relative bg-white rounded-[40px] px-8 py-6 shadow-sm border border-[#E8E8E8]">
              <span className="text-sm font-semibold text-gray-800 block mb-4">Implants</span>
              {!implantSectionExpanded ? (
                <button
                  type="button"
                  onClick={() => {
                    setImplantSectionExpanded(true);
                    setImplantRows((prev) => (prev.length ? prev : [{ name: "", cost: "" }]));
                  }}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-[54px] border-2 border-solid border-[#5AC69F] bg-white text-[#5AC69F] font-lufga font-medium hover:bg-[#F4FBF8] transition-colors"
                >
                  <span className="text-xl">+</span>
                  ADD MORE
                </button>
              ) : (
                <>
                  {implantRows.map((row, index) => (
                    <div key={index} className="flex gap-6 mb-4 items-center">
                      <div className="flex-1">
                        <Input
                          label="Implant Name"
                          placeholder=""
                          variant="implant"
                          value={row.name}
                          onChange={(e) => {
                            const next = [...implantRows];
                            next[index] = { ...next[index], name: e.target.value };
                            setImplantRows(next);
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <Input
                          label="Implant Cost"
                          placeholder=""
                          variant="implant"
                          value={row.cost}
                          onChange={(e) => {
                            const next = [...implantRows];
                            next[index] = {
                              ...next[index],
                              cost: sanitizeDecimalCostInput(e.target.value),
                            };
                            setImplantRows(next);
                          }}
                        />
                      </div>
                      <img
                        src={deleteIcon}
                        alt="delete"
                        className="w-5 h-5 flex-shrink-0 cursor-pointer hover:opacity-70 transition"
                        onClick={() => removeImplantRow(index)}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setImplantRows((prev) => [...prev, { name: "", cost: "" }])
                    }
                    className="mt-2 flex items-center justify-center gap-2 py-2 rounded-full border-2 border-solid border-[#5AC69F] bg-white text-[#5AC69F] font-lufga text-sm font-medium hover:bg-[#F4FBF8] transition-colors"
                  >
                    <span>+</span>
                    ADD MORE
                  </button>
                </>
              )}
            </div>
          )}

          {/* Stratification Yes/No — default Yes when master isStratification is true; user may choose No for simple tier costing */}
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">Stratification ?</span>
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setStratification(!stratification);
              }}
              onClick={() => setStratification(!stratification)}
              className="relative w-32 h-9 rounded-full border-2 border-[#5AC69F] bg-white cursor-pointer"
            >
              <div
                className={`absolute top-0 left-0 h-full w-1/2 rounded-full bg-[#5AC69FCC] transition-transform duration-300 ${
                  stratification ? "translate-x-0" : "translate-x-full"
                }`}
              />
              <div className="relative z-10 flex h-full text-sm font-medium">
                <div
                  className={`flex-1 flex items-center justify-center ${
                    stratification ? "text-black" : "text-gray-600"
                  }`}
                >
                  Yes
                </div>
                <div
                  className={`flex-1 flex items-center justify-center ${
                    !stratification ? "text-black" : "text-gray-600"
                  }`}
                >
                  No
                </div>
              </div>
            </div>
          </div>

          {/* Tiers */}
          {tiers.map((tier, index) => (
            <div key={tier.tierNo}>
              {stratification ? (
                <TierBlock
                  title={`Tier ${tier.tierNo} Costing`}
                  rows={tier.rows}
                  onChangeRow={(rowIndex, field, value) => {
                    const nextVal =
                      field === "cost" ? sanitizeDecimalCostInput(value) : value;
                    const updated = [...tiers];
                    const nextRows = [...(updated[index].rows || [])];
                    nextRows[rowIndex] = {
                      ...nextRows[rowIndex],
                      [field]: nextVal,
                    };
                    updated[index].rows = nextRows;
                    setTiers(updated);
                  }}
                  onRemoveTier={null}
                  deleteIconSrc={deleteIcon}
                />
              ) : (
                <div className="flex gap-4 items-center">
                  <div className="flex-1">
                    <Input
                      label={`Tier ${tier.tierNo} Costing`}
                      placeholder=""
                      value={tier.cost}
                      onChange={(e) => {
                        const updated = [...tiers];
                        updated[index] = {
                          ...updated[index],
                          cost: sanitizeDecimalCostInput(e.target.value),
                        };
                        setTiers(updated);
                      }}
                    />
                  </div>
                  {/* Temporarily hidden — tier delete not needed for now
                  {tiers.length > 1 && (
                    <img
                      src={deleteIcon}
                      alt="delete"
                      className="w-5 h-5 flex-shrink-0 cursor-pointer hover:opacity-70 transition"
                      onClick={() => removeTier(index)}
                    />
                  )}
                  */}
                </div>
              )}
            </div>
          ))}

          {/* Save */}
          <div className="flex justify-center pt-6 font-lufga">
            <button
              type="button"
              onClick={handleSave}
              disabled={
                saving || !hasUnsavedChanges || !hasAnyFilledCostField
              }
              className="w-[380px] h-[80px] bg-[#5AC69FCC] text-black rounded-full text-lg font-medium shadow border-0 flex items-center justify-center gap-2 font-lufga disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <span className="inline-block w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TierBlock({ title, rows, onChangeRow, onRemoveTier, deleteIconSrc }) {
  return (
    <div className="relative bg-white rounded-3xl px-6 py-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">{title}</span>
        {/* Temporarily hidden — tier delete not needed for now
        {onRemoveTier && (
          <img
            src={deleteIconSrc}
            alt="delete"
            className="w-5 h-5 cursor-pointer hover:opacity-70 transition"
            onClick={onRemoveTier}
          />
        )}
        */}
      </div>
      {(rows || []).map((row, index) => (
        <div key={index} className="flex gap-6 items-center">
          <div className="flex-1">
            <Input
              label="Stratification Name"
              placeholder=""
              variant="implant"
              value={row?.name ?? ""}
              onChange={(e) => onChangeRow?.(index, "name", e.target.value)}
            />
          </div>
          <div className="flex-1">
            <Input
              label="Stratification Cost"
              placeholder=""
              variant="implant"
              value={row?.cost ?? ""}
              onChange={(e) => onChangeRow?.(index, "cost", e.target.value)}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function StateDropdown({ value, onChange, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-xs">
      <div
        onClick={() => !disabled && setOpen(!open)}
        className={`relative w-full rounded-full px-6 py-4 shadow-sm flex items-center justify-between gap-[5px] ${
          disabled ? "bg-[#E8E8E8] cursor-not-allowed" : "bg-white cursor-pointer"
        }`}
      >
        <span className="text-[20px] text-gray-700 font-medium">
          {value || placeholder}
        </span>
        {!disabled && (
          <img
            src={downArrowNew}
            alt=""
            width={22}
            height={12}
            className={`shrink-0 opacity-100 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden
          />
        )}
      </div>
      {!disabled && open && (
        <div className="absolute z-20 left-0 right-0 mt-2 bg-white rounded-2xl shadow-lg max-h-64 overflow-y-auto">
          {INDIAN_STATES.map((name) => (
            <div
              key={name}
              onClick={() => {
                onChange(name);
                setOpen(false);
              }}
              className="px-6 py-3 text-sm hover:bg-[#F4FBF8] cursor-pointer"
            >
              {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Input({ placeholder, label, variant = "default", value, onChange }) {
  const base = "w-full text-sm font-lufga outline-none";
  const variants = {
    default: "bg-white border border-solid border-[#E8E8E899] rounded-full px-6 py-4",
    implant: "border border-solid border-[#E8E8E899] bg-white rounded-full px-6 py-4",
  };

  if (label) {
    return (
      <div className="rounded-full border border-solid border-[#E8E8E899] bg-white px-6 py-3 min-h-[56px] flex flex-col justify-center">
        <div className="text-xs text-gray-500 font-medium mb-1">{label}</div>
        <input
          type="text"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full text-sm font-lufga outline-none border-none bg-transparent p-0 placeholder-gray-400"
        />
      </div>
    );
  }

  return (
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`${base} ${variants[variant]}`}
    />
  );
}

function Dropdown({
  placeholder,
  options = [],
  className = "",
  disabled = false,
  value: controlledValue,
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(controlledValue || "");
  const ref = useRef(null);

  useEffect(() => {
    if (controlledValue !== undefined) setValue(controlledValue);
  }, [controlledValue]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div
      ref={ref}
      onClick={() => !disabled && setOpen(!open)}
      className={`relative w-full rounded-full px-6 py-4 shadow-sm ${
        disabled ? "bg-[#E8E8E8] cursor-not-allowed text-xl" : "bg-white cursor-pointer"
      } ${className}`}
    >
      <span className="text-[20px] text-gray-700 font-medium">
        {value || placeholder}
      </span>
      {!disabled && (
        <img
          src={downArrowNew}
          alt=""
          width={22}
          height={12}
          className={`absolute right-6 top-1/2 -translate-y-1/2 shrink-0 opacity-100 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      )}
      {!disabled && open && options.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-3 bg-white rounded-2xl shadow-lg">
          {options.map((opt) => (
            <div
              key={opt}
              onClick={() => {
                setValue(opt);
                setOpen(false);
              }}
              className="px-6 py-3 text-sm hover:bg-[#F4FBF8]"
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
